import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
export interface TableColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
  mobileLabel?: string; // Custom label for mobile view
  hideOnMobile?: boolean; // Hide this column on mobile
}
export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}
export function Table<T extends Record<string, unknown>>({
  columns,
  data,
  keyExtractor,
  onSort,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = ''
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleSort = (key: string) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  };
  const alignStyles = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  // Desktop table view
  const DesktopTable = () => <div className="hidden md:block overflow-x-auto" data-id="element-547">
      <table className="w-full" data-id="element-548">
        <thead data-id="element-549">
          <tr className="border-b border-surface-200" data-id="element-550">
            {columns.map(column => <th key={column.key} className={`
                  px-4 py-3 text-body-sm font-semibold text-surface-600
                  ${alignStyles[column.align || 'left']}
                  ${column.width || ''}
                `} data-id="element-551">
                {column.sortable ? <button onClick={() => handleSort(column.key)} className="inline-flex items-center gap-1 hover:text-surface-900 transition-colors" data-id="element-552">
                    {column.header}
                    <span className="flex flex-col" data-id="element-553">
                      <ChevronUpIcon className={`w-3 h-3 -mb-1 ${sortKey === column.key && sortDirection === 'asc' ? 'text-primary-500' : 'text-surface-300'}`} data-id="element-554" />
                      <ChevronDownIcon className={`w-3 h-3 ${sortKey === column.key && sortDirection === 'desc' ? 'text-primary-500' : 'text-surface-300'}`} data-id="element-555" />
                    </span>
                  </button> : column.header}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-556">
          {loading ? Array.from({
          length: 5
        }).map((_, index) => <tr key={index} className="border-b border-surface-100" data-id="element-557">
                {columns.map(column => <td key={column.key} className="px-4 py-4" data-id="element-558">
                    <div className="h-4 bg-surface-100 rounded animate-pulse" data-id="element-559" />
                  </td>)}
              </tr>) : data.length === 0 ? <tr data-id="element-560">
              <td colSpan={columns.length} className="px-4 py-12 text-center text-body-md text-surface-500" data-id="element-561">
                {emptyMessage}
              </td>
            </tr> : data.map((row, rowIndex) => <motion.tr key={keyExtractor(row)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: rowIndex * 0.03
        }} onClick={() => onRowClick?.(row)} className={`
                  border-b border-surface-100
                  ${onRowClick ? 'cursor-pointer hover:bg-surface-50 transition-colors' : ''}
                `} data-id="element-562">
                {columns.map(column => <td key={column.key} className={`
                      px-4 py-4 text-body-md text-surface-700
                      ${alignStyles[column.align || 'left']}
                    `} data-id="element-563">
                    {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key] as React.ReactNode}
                  </td>)}
              </motion.tr>)}
        </tbody>
      </table>
    </div>;
  // Mobile card view
  const MobileCards = () => <div className="md:hidden space-y-3" data-id="element-564">
      {loading ? Array.from({
      length: 3
    }).map((_, index) => <div key={index} className="p-4 bg-white rounded-xl border border-surface-200" data-id="element-565">
            <div className="space-y-3" data-id="element-566">
              {columns.filter(col => !col.hideOnMobile).slice(0, 4).map(column => <div key={column.key} className="flex justify-between items-start" data-id="element-567">
                    <div className="h-4 w-20 bg-surface-100 rounded animate-pulse" data-id="element-568" />
                    <div className="h-4 w-32 bg-surface-100 rounded animate-pulse" data-id="element-569" />
                  </div>)}
            </div>
          </div>) : data.length === 0 ? <div className="p-8 text-center text-body-md text-surface-500 bg-surface-50 rounded-xl" data-id="element-570">
          {emptyMessage}
        </div> : data.map((row, rowIndex) => <motion.div key={keyExtractor(row)} initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: rowIndex * 0.05
    }} onClick={() => onRowClick?.(row)} className={`
              p-4 bg-white rounded-xl border border-surface-200 shadow-sm
              ${onRowClick ? 'active:scale-[0.98] cursor-pointer' : ''}
            `} data-id="element-571">
            <div className="space-y-3" data-id="element-572">
              {columns.filter(col => !col.hideOnMobile).map(column => <div key={column.key} className="flex justify-between items-start gap-4" data-id="element-573">
                    <span className="text-body-sm font-medium text-surface-500 flex-shrink-0" data-id="element-574">
                      {column.mobileLabel || column.header}
                    </span>
                    <span className="text-body-sm text-surface-900 text-right" data-id="element-575">
                      {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key] as React.ReactNode}
                    </span>
                  </div>)}
            </div>
          </motion.div>)}
    </div>;
  return <div className={className} data-id="element-576">
      <DesktopTable data-id="element-577" />
      <MobileCards data-id="element-578" />
    </div>;
}